// Drag-to-resize for the desktop popup panel.
//
// Native CSS `resize` can't be used here: (a) our layout props are set
// `!important` to survive hostile host CSS, which locks the box, and (b) the
// panel iframe swallows pointer events the moment the cursor crosses into it.
// So we draw our own grab handle at the popup's *free-growing* corner and
// translate pointer moves into width/height. The chosen size is persisted to
// localStorage so it survives reloads.

import type { WidgetMode, WidgetPosition } from '@/domain';

import { setImportant } from './style';

const STORE_KEY = 'ba-assistant-popup-size';
export const MIN_W = 320;
export const MIN_H = 360;
/** Hard ceiling so the popup never feels like fullscreen by accident. */
const MAX_W = 720;

export interface PopupSize {
  width: number;
  height: number;
}

/** Clamp a candidate size to [MIN, viewport-bounded MAX]. */
export function clampSize(w: number, h: number, win: Window): PopupSize {
  const maxW = Math.max(MIN_W, Math.min(win.innerWidth - 32, MAX_W));
  // 104 = popup bottom gap (92) + top gap (12); matches the CSS max-height so a
  // restored size never renders shorter than its stored value.
  const maxH = Math.max(MIN_H, win.innerHeight - 104);
  return {
    width: Math.round(Math.min(Math.max(w, MIN_W), maxW)),
    height: Math.round(Math.min(Math.max(h, MIN_H), maxH)),
  };
}

export function loadStoredSize(win: Window = window): PopupSize | null {
  try {
    const raw = win.localStorage.getItem(STORE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as PopupSize).width === 'number' &&
      typeof (parsed as PopupSize).height === 'number'
    ) {
      return { width: (parsed as PopupSize).width, height: (parsed as PopupSize).height };
    }
  } catch {
    /* localStorage blocked or corrupt — fall back to defaults */
  }
  return null;
}

export function saveStoredSize(size: PopupSize, win: Window = window): void {
  try {
    win.localStorage.setItem(STORE_KEY, JSON.stringify(size));
  } catch {
    /* ignore */
  }
}

// Subtle diagonal grip mark.
const GRIP_SVG =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M11 1 L1 11 M11 5 L5 11 M11 9 L9 11" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
  '</svg>';

export interface ResizeDeps {
  frameWrap: HTMLElement;
  iframe: HTMLElement;
  getPosition: () => WidgetPosition;
  getMode: () => WidgetMode;
  /** Persist + apply a committed size (called on each move + on drag end). */
  applySize: (size: PopupSize) => void;
}

/**
 * Create + wire the resize handle, append it to the frame wrapper, and return
 * it. The caller owns showing/hiding it per mode via `setHandleVisible`.
 */
export function attachResizeHandle(deps: ResizeDeps, doc: Document = document): HTMLElement {
  const handle = doc.createElement('div');
  handle.id = 'ba-assistant-resize-handle';
  handle.setAttribute('aria-hidden', 'true');
  handle.innerHTML = GRIP_SVG;
  setImportant(handle, {
    position: 'absolute',
    width: '20px',
    height: '20px',
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    'z-index': '3',
    color: 'rgba(15, 23, 42, 0.4)',
    'touch-action': 'none',
    'user-select': 'none',
  });
  positionHandle(handle, deps.getPosition());

  let dragging = false;
  let rectAtStart: DOMRect | null = null;

  const onMove = (e: PointerEvent): void => {
    if (!dragging || rectAtStart === null) return;
    const win = doc.defaultView ?? window;
    const left = deps.getPosition() === 'bottom-right'
      ? rectAtStart.right - e.clientX
      : e.clientX - rectAtStart.left;
    const height = rectAtStart.bottom - e.clientY;
    deps.applySize(clampSize(left, height, win));
  };

  const onUp = (e: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    rectAtStart = null;
    // Restore pointer events on the iframe so the panel is interactive again.
    setImportant(deps.iframe, { 'pointer-events': 'auto' });
    try {
      handle.releasePointerCapture(e.pointerId);
    } catch {
      /* capture may already be gone */
    }
    handle.removeEventListener('pointermove', onMove);
    handle.removeEventListener('pointerup', onUp);
  };

  handle.addEventListener('pointerdown', (e) => {
    if (deps.getMode() !== 'popup') return;
    e.preventDefault();
    dragging = true;
    rectAtStart = deps.frameWrap.getBoundingClientRect();
    // Block the iframe from eating pointermove while dragging over it.
    setImportant(deps.iframe, { 'pointer-events': 'none' });
    try {
      handle.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture unsupported (e.g. jsdom) — moves still work */
    }
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
  });

  deps.frameWrap.appendChild(handle);
  return handle;
}

/** Anchor the handle to the corner that grows the popup (opposite its anchor). */
export function positionHandle(handle: HTMLElement, position: WidgetPosition): void {
  if (position === 'bottom-right') {
    // Popup anchored bottom-right → grows up-left → handle at top-left.
    setImportant(handle, { top: '2px', left: '2px', right: 'auto', cursor: 'nwse-resize' });
  } else {
    // Popup anchored bottom-left → grows up-right → handle at top-right.
    setImportant(handle, { top: '2px', right: '2px', left: 'auto', cursor: 'nesw-resize' });
  }
}

export function setHandleVisible(handle: HTMLElement, visible: boolean): void {
  setImportant(handle, { display: visible ? 'flex' : 'none' });
}
